import { type FC, type ReactNode } from 'react';

import { type Breakpoint } from '@mui/material/styles';

import { type QuantitativeSize } from '@craft/ui';
import { Stack, type StackProps } from '@craft/ui/components/structural';
import { isBreakpoint, quantitativeSizeToString } from '@craft/ui/util';

export interface PageProps extends Pick<
  StackProps,
  'alignItems' | 'gap' | 'justifyContent' | 'paddingTop'
> {
  readonly children: ReactNode;
  /**
   * Optionally defines the maximum width of the {@link Page}, in cases where the content on the
   * page should be horizontally centered and fill the entire available width inside of the
   * {@link Layout} up until this maximum desired width.
   *
   * When this prop is specified, the {@link Page} will behave similarly to MUI's {@link Container}
   * component.
   *
   * This prop can be specified as either a {@link Breakpoint} or a concrete, pixel-valued size,
   * {@link QuantitativeSize<'px'>}.  It should never be defined as a size that is a percent
   * value relative to the overall available width.
   *
   * @default 100%
   */
  readonly maxWidth?: Breakpoint | QuantitativeSize<'px'>;
}

/**
 * A component that should be used to wrap the main content of all pages in the application that
 * are using the new layout system, {@link PageLayoutVersion.Version2}.  This component should
 * be leveraged (the vast majority of the time) inside of the elements rendered by the
 * {@link AppPage.getNestedLayout} method, for pages, {@link AppPage}, that are rendered inside of
 * the NextJS pages directory.
 *
 * It is critically important that the height and width related constraints are not overridden
 * for this component.
 *
 * At least as of right now, this component should *never* dictate scroll behavior.  In cases where
 * the overall page needs to be scrollable, the scroll behavior should be dictated by the outer
 * {@link Layout} component, by attributing the NextJS page, {@link AppPage}, with the
 * 'isLayoutScrollable' property.  In cases where the outer page layout should not be scrollable,
 * but certain content inside of the {@link Page} should be scrollable, that scroll behavior
 * should be dictated by those inner elements and not the {@link Page} itself.
 */
export const Page: FC<PageProps> = ({ children, maxWidth, ...props }) => (
  <Stack
    {...props}
    height='100%'
    marginX={maxWidth ? 'auto' : undefined}
    maxHeight='100%'
    maxWidth={
      isBreakpoint(maxWidth)
        ? maxWidth
        : maxWidth
          ? quantitativeSizeToString(maxWidth, 'px')
          : '100%'
    }
    minHeight='100%'
    minWidth={maxWidth === undefined ? '100%' : undefined}
    width='100%'
  >
    {children}
  </Stack>
);
